/**
 * TypeScript types for Live Location Activity Dashboard
 * Matches backend DTOs from BizClick.WMS.Shared.DTOs.LocationActivityDTOs
 */

/**
 * Activity type classification with distinct colors
 * Matches backend ActivityType enum
 */
export enum ActivityType {
  Pick = 1,            // Plukking (TransType 10)
  PutAway = 2,         // Utlegg / Plassering (TransType 12)
  Transfer = 3,        // DEPRECATED — beholdt for cachede events. Bruk de spesifikke nye verdiene.
  CorrectionOut = 4,   // Korreksjon ut (TransType 1/5-/15-)
  CorrectionIn = 5,    // Korreksjon inn (TransType 2/5+/15+)
  Counting = 6,        // Telling (TransType 100/4)
  Receiving = 7,       // Varemottak (TransType 20)
  Reservation = 8,     // Reservasjon (ikke i bruk per i dag)
  StockTransfer = 9,   // Lageroverføring mellom fysiske bins (TransType 11)
  Replenishment = 10,  // Påfylling av plukklokasjon (TransType 13)
  Optimization = 11,   // Optimalisering av lager (TransType 14)
  InternalMove = 12,   // Internflytt operatør/transitt (TransType 17)
  OpeningBalance = 13  // Inngående saldo (TransType 88)
}

/**
 * Activity event broadcasted from SignalR
 * Matches backend LocationActivityEvent
 */
export interface LocationActivityEvent {
  binCode: string;
  rackNumber: number;
  activityType: ActivityType;
  timestamp: string; // ISO 8601 format
  operatorId: string;
  itemNo: string;
  quantity: number;
  companyNo: string;
  customerNo: string;
  warehouseId: string;
}

/**
 * Statistics about location activity
 * Matches backend LocationActivityStats
 */
export interface LocationActivityStats {
  companyNo: string;
  warehouseId: string;
  totalActivitiesLast5Minutes: number;
  activeOperators: number;
  activeLocations: number;
  currentQueueSize: number;
  lastActivityTimestamp: string; // ISO 8601 format
}

/**
 * SignalR connection states
 */
export enum ConnectionStatus {
  Connected = 'Connected',
  Reconnecting = 'Reconnecting',
  Disconnected = 'Disconnected'
}

/**
 * Connection quality metrics
 */
export interface ConnectionQuality {
  latency: number; // milliseconds
  packetLoss: number; // percentage 0-100
  lastPingTimestamp?: string;
}

/**
 * Hook return type for useLocationActivity
 */
export interface UseLocationActivityReturn {
  // Connection state
  connectionStatus: ConnectionStatus;
  connectionQuality: ConnectionQuality;
  
  // Event handling
  lastEvent: LocationActivityEvent | null;
  
  // Methods
  joinWarehouseGroup: (warehouseId: string) => Promise<void>;
  joinRackGroup: (warehouseId: string, rackNumber: number) => Promise<void>;
  leaveGroup: (groupName: string) => Promise<void>;
  disconnect: () => void;
}

/**
 * Activity type to color mapping for UI
 * Updated: PutAway now uses blue to distinguish from Receiving (green)
 */
export const ActivityTypeColors = {
  [ActivityType.Pick]: 'bg-orange-500/30',
  [ActivityType.PutAway]: 'bg-blue-500/30',
  [ActivityType.Transfer]: 'bg-purple-500/30',          // DEPRECATED — beholdt for cachede events
  [ActivityType.CorrectionOut]: 'bg-red-500/30',
  [ActivityType.CorrectionIn]: 'bg-emerald-500/30',
  [ActivityType.Counting]: 'bg-slate-500/30',
  [ActivityType.Receiving]: 'bg-green-500/30',
  [ActivityType.Reservation]: 'bg-yellow-500/30',
  [ActivityType.StockTransfer]: 'bg-sky-500/30',        // Fysisk lageroverføring
  [ActivityType.Replenishment]: 'bg-teal-500/30',       // Påfylling
  [ActivityType.Optimization]: 'bg-indigo-500/30',      // Optimalisering
  [ActivityType.InternalMove]: 'bg-purple-500/30',      // Internflytt (samme lilla som gamle Transfer)
  [ActivityType.OpeningBalance]: 'bg-stone-500/30'      // Inngående saldo
} as const;

/**
 * Detailed color classes for rich activity cards
 * Provides full styling including gradients, borders, and icon colors
 */
export const ActivityTypeDetailedColors = {
  [ActivityType.Pick]: {
    bg: 'bg-gradient-to-br from-orange-50 to-orange-50/30',
    border: 'border-l-4 border-orange-500',
    icon: 'bg-orange-500 text-white',
    badge: 'bg-orange-100 text-orange-700',
    text: 'text-orange-700'
  },
  [ActivityType.PutAway]: {
    bg: 'bg-gradient-to-br from-blue-50 to-blue-50/30',
    border: 'border-l-4 border-blue-500',
    icon: 'bg-blue-500 text-white',
    badge: 'bg-blue-100 text-blue-700',
    text: 'text-blue-700'
  },
  [ActivityType.Transfer]: {
    bg: 'bg-gradient-to-br from-purple-50 to-purple-50/30',
    border: 'border-l-4 border-purple-500',
    icon: 'bg-purple-500 text-white',
    badge: 'bg-purple-100 text-purple-700',
    text: 'text-purple-700'
  },
  [ActivityType.CorrectionOut]: {
    bg: 'bg-gradient-to-br from-red-50 to-red-50/30',
    border: 'border-l-4 border-red-500',
    icon: 'bg-red-500 text-white',
    badge: 'bg-red-100 text-red-700',
    text: 'text-red-700'
  },
  [ActivityType.CorrectionIn]: {
    bg: 'bg-gradient-to-br from-emerald-50 to-emerald-50/30',
    border: 'border-l-4 border-emerald-500',
    icon: 'bg-emerald-500 text-white',
    badge: 'bg-emerald-100 text-emerald-700',
    text: 'text-emerald-700'
  },
  [ActivityType.Counting]: {
    bg: 'bg-gradient-to-br from-slate-50 to-slate-50/30',
    border: 'border-l-4 border-slate-500',
    icon: 'bg-slate-500 text-white',
    badge: 'bg-slate-100 text-slate-700',
    text: 'text-slate-700'
  },
  [ActivityType.Receiving]: {
    bg: 'bg-gradient-to-br from-green-50 to-green-50/30',
    border: 'border-l-4 border-green-500',
    icon: 'bg-green-500 text-white',
    badge: 'bg-green-100 text-green-700',
    text: 'text-green-700'
  },
  [ActivityType.Reservation]: {
    bg: 'bg-gradient-to-br from-yellow-50 to-yellow-50/30',
    border: 'border-l-4 border-yellow-500',
    icon: 'bg-yellow-500 text-white',
    badge: 'bg-yellow-100 text-yellow-700',
    text: 'text-yellow-700'
  },
  [ActivityType.StockTransfer]: {
    bg: 'bg-gradient-to-br from-sky-50 to-sky-50/30',
    border: 'border-l-4 border-sky-500',
    icon: 'bg-sky-500 text-white',
    badge: 'bg-sky-100 text-sky-700',
    text: 'text-sky-700'
  },
  [ActivityType.Replenishment]: {
    bg: 'bg-gradient-to-br from-teal-50 to-teal-50/30',
    border: 'border-l-4 border-teal-500',
    icon: 'bg-teal-500 text-white',
    badge: 'bg-teal-100 text-teal-700',
    text: 'text-teal-700'
  },
  [ActivityType.Optimization]: {
    bg: 'bg-gradient-to-br from-indigo-50 to-indigo-50/30',
    border: 'border-l-4 border-indigo-500',
    icon: 'bg-indigo-500 text-white',
    badge: 'bg-indigo-100 text-indigo-700',
    text: 'text-indigo-700'
  },
  [ActivityType.InternalMove]: {
    bg: 'bg-gradient-to-br from-purple-50 to-purple-50/30',
    border: 'border-l-4 border-purple-500',
    icon: 'bg-purple-500 text-white',
    badge: 'bg-purple-100 text-purple-700',
    text: 'text-purple-700'
  },
  [ActivityType.OpeningBalance]: {
    bg: 'bg-gradient-to-br from-stone-50 to-stone-50/30',
    border: 'border-l-4 border-stone-500',
    icon: 'bg-stone-500 text-white',
    badge: 'bg-stone-100 text-stone-700',
    text: 'text-stone-700'
  }
} as const;

/**
 * Activity type to display name mapping (Norwegian)
 */
export const ActivityTypeNames = {
  [ActivityType.Pick]: 'Plukking',
  [ActivityType.PutAway]: 'Utlegg',
  [ActivityType.Transfer]: 'Overføring',          // DEPRECATED — kun for eldre cachede events
  [ActivityType.CorrectionOut]: 'Korreksjon Ut',
  [ActivityType.CorrectionIn]: 'Korreksjon Inn',
  [ActivityType.Counting]: 'Telling',
  [ActivityType.Receiving]: 'Mottak',
  [ActivityType.Reservation]: 'Reservasjon',
  [ActivityType.StockTransfer]: 'Lageroverføring',
  [ActivityType.Replenishment]: 'Påfylling',
  [ActivityType.Optimization]: 'Optimalisering',
  [ActivityType.InternalMove]: 'Internflytt',
  [ActivityType.OpeningBalance]: 'Inngående saldo'
} as const;
